import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { EASE, scrollToHash } from "@/components/Reveal";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Manifesto", href: "#manifesto" },
  { label: "Network", href: "#network" },
  { label: "Contact", href: "#contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.2, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-500 ${
          scrolled
            ? "border-white/10 bg-black/60 backdrop-blur-xl"
            : "border-transparent bg-transparent"
        }`}
      >
        <div className="flex h-16 items-center justify-between px-6 lg:h-20 lg:px-12">
          <a
            href="#top"
            data-testid="nav-logo"
            onClick={(e) => {
              e.preventDefault();
              window.__lenis
                ? window.__lenis.scrollTo(0, { duration: 1.4 })
                : window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="font-display text-xl font-semibold uppercase tracking-tight"
          >
            PORTWAVE<span className="text-[#FF3B30]">.</span>
          </a>

          <nav className="hidden items-center gap-10 md:flex" aria-label="Primary">
            {LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                onClick={(e) => go(e, link.href)}
                className="group relative text-xs font-semibold uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:text-white"
              >
                {link.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-full origin-left scale-x-0 bg-[#FF3B30] transition-transform duration-300 group-hover:scale-x-100" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#contact"
              data-testid="nav-cta-quote"
              onClick={(e) => go(e, "#contact")}
              className="group relative hidden overflow-hidden border border-white/20 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] md:inline-flex"
            >
              <span className="absolute inset-0 translate-y-full bg-[#FF3B30] transition-transform duration-300 ease-out group-hover:translate-y-0" />
              <span className="relative z-10 flex items-center gap-2 transition-colors duration-300">
                Get a quote
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-45" />
              </span>
            </a>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center border border-white/20 md:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#050505]/95 px-8 backdrop-blur-xl md:hidden"
            data-testid="mobile-menu"
          >
            {LINKS.map((link, i) => (
              <div key={link.href} className="overflow-hidden border-b border-white/10">
                <motion.a
                  href={link.href}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  onClick={(e) => go(e, link.href)}
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ duration: 0.6, delay: 0.08 * i, ease: EASE }}
                  className="block py-5 font-display text-4xl font-semibold uppercase tracking-tight"
                >
                  {link.label}
                </motion.a>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
